import { createClient } from '@supabase/supabase-js';

export const SUPABASE_URL = 'https://ggsgnuzbavpbwyrcyqjg.supabase.co';
export const SUPABASE_ANON_KEY = 'sb_publishable_o4ralMWBTBZ66fOVneorNA_nuzBEoho';

let cachedClient = null;
let cachedSignature = '';

function safeParseJson(raw) {
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function bindGlobal(client) {
  if (typeof window === 'undefined' || !client) return client;
  try {
    window.kifakaSupabase = client;
    window.supabase = client;
  } catch (_err) {
    // noop
  }
  return client;
}

function readStorageValue(key) {
  if (typeof window === 'undefined' || !key) return '';
  try {
    return String(window.localStorage?.getItem(key) || '').trim();
  } catch (_err) {
    return '';
  }
}

function readGlobalValue(key) {
  if (typeof window === 'undefined' || !key) return '';
  try {
    return String(window[key] || '').trim();
  } catch (_err) {
    return '';
  }
}

function readParamValue(key) {
  if (typeof window === 'undefined' || !key) return '';
  try {
    const params = new URLSearchParams(window.location?.search || '');
    return String(params.get(key) || '').trim();
  } catch (_err) {
    return '';
  }
}

function resolveSupabaseConfig() {
  const globalUrl = readGlobalValue('__KIFAKA_SUPABASE_URL');
  const globalKey = readGlobalValue('__KIFAKA_SUPABASE_ANON_KEY');
  const paramUrl = readParamValue('kfk_sb_url') || readParamValue('sb_url');
  const paramKey = readParamValue('kfk_sb_key') || readParamValue('sb_key');
  const lsUrl = readStorageValue('kifaka_supabase_url');
  const lsKey = readStorageValue('kifaka_supabase_anon_key');

  const url = globalUrl || paramUrl || lsUrl || SUPABASE_URL;
  const key = globalKey || paramKey || lsKey || SUPABASE_ANON_KEY;
  return { url, key };
}

export function getSupabaseConfig() {
  return resolveSupabaseConfig();
}

export function getSupabaseClient() {
  const { url, key } = resolveSupabaseConfig();
  const signature = `${url}::${key}`;

  if (cachedClient && cachedSignature === signature) {
    return bindGlobal(cachedClient);
  }

  if (typeof window !== 'undefined') {
    const globalClient = window.kifakaSupabase || window.supabase;
    if (globalClient && cachedSignature === signature) {
      cachedClient = globalClient;
      return bindGlobal(cachedClient);
    }
  }

  cachedClient = createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    }
  });
  cachedSignature = signature;
  return bindGlobal(cachedClient);
}

export async function getSupabaseSession() {
  const client = getSupabaseClient();
  if (!client?.auth) return null;
  try {
    const { data } = await client.auth.getSession();
    return data?.session || null;
  } catch (_err) {
    return null;
  }
}

export async function getSupabaseUser() {
  const client = getSupabaseClient();
  if (!client?.auth) return null;

  const session = await getSupabaseSession();
  if (session?.user) return session.user;

  try {
    const { data } = await client.auth.getUser();
    return data?.user || null;
  } catch (_err) {
    return null;
  }
}

export function getStoredSupabaseSessionSnapshot() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = String(window.localStorage.key(index) || '');
      if (!/^sb-.*-auth-token$/u.test(key)) continue;
      const payload = safeParseJson(window.localStorage.getItem(key) || '');
      if (!payload || typeof payload !== 'object') continue;
      const accessToken = String(payload.access_token || '').trim();
      const user = payload.user && typeof payload.user === 'object' ? payload.user : null;
      if (!accessToken || !user?.id) continue;
      return payload;
    }
  } catch (_err) {
    return null;
  }
  return null;
}
